import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, isAuthenticated } from '../App';
import { Search, MapPin, Building2, Clock, Briefcase, ChevronRight, X, Home, Laptop, Users, Lock, Star, CheckCircle, Bell, Shield, Zap, HeadphonesIcon, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { AdSlot } from '../components/ads/AdSlot';
import { InstallPrompt } from '../components/mobile/InstallPrompt';

const PublicHome = () => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [locationQuery, setLocationQuery] = useState('');
  const [footerConfig, setFooterConfig] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [popupAnnouncement, setPopupAnnouncement] = useState(null);
  const [banners, setBanners] = useState([]);
  const [stats, setStats] = useState({
    active_jobs: 0,
    verified_employers: 0,
    job_seekers: 0,
    user_rating: 4.8
  });
  
  // Limit jobs shown to 6 for non-logged in users
  const MAX_VISIBLE_JOBS = 6;

  useEffect(() => {
    // If already logged in, redirect to appropriate dashboard
    if (isAuthenticated()) {
      const token = localStorage.getItem('token');
      try {
        const decoded = JSON.parse(atob(token.split('.')[1]));
        if (decoded.role === 'candidate') {
          navigate('/candidate-dashboard');
        } else {
          navigate('/dashboard');
        }
      } catch {
        // Continue to show public page
      }
    }
    
    fetchData();
    checkPopupAnnouncement();
    
    // Track page visit
    trackPageVisit('home');
  }, [navigate]);

  // Track page visit
  const trackPageVisit = async (page) => {
    try {
      const sessionId = sessionStorage.getItem('visitor_session') || `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      if (!sessionStorage.getItem('visitor_session')) {
        sessionStorage.setItem('visitor_session', sessionId);
      }
      await axios.post(`${API}/track/visit`, {
        page,
        referrer: document.referrer,
        session_id: sessionId
      });
    } catch (e) {
      // Silent fail for tracking
    }
  };

  // Track search
  const trackSearch = async (query, resultsCount) => {
    try {
      const sessionId = sessionStorage.getItem('visitor_session');
      await axios.post(`${API}/track/search`, {
        query,
        results_count: resultsCount,
        session_id: sessionId
      });
    } catch (e) {
      // Silent fail
    }
  };

  const fetchData = async () => {
    try {
      const [jobsRes, footerRes, bannersRes, statsRes] = await Promise.all([
        axios.get(`${API}/jobs/public`),
        axios.get(`${API}/footer-config`),
        axios.get(`${API}/banners/public/candidate`).catch(() => ({ data: [] })),
        axios.get(`${API}/public/stats`).catch(() => ({ data: { active_jobs: 0, verified_employers: 0, job_seekers: 0, user_rating: 4.8 } }))
      ]);
      setJobs(jobsRes.data);
      setFooterConfig(footerRes.data);
      setBanners(bannersRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkPopupAnnouncement = async () => {
    try {
      const res = await axios.get(`${API}/popup-announcement/active`);
      if (res.data && res.data.is_active) {
        const dismissedPopups = JSON.parse(localStorage.getItem('dismissed_popups') || '[]');
        if (!dismissedPopups.includes(res.data.id)) {
          setPopupAnnouncement(res.data);
        }
      }
    } catch (e) {
      // Silent fail
    }
  };

  const dismissPopup = () => {
    if (popupAnnouncement) {
      const dismissedPopups = JSON.parse(localStorage.getItem('dismissed_popups') || '[]');
      dismissedPopups.push(popupAnnouncement.id);
      localStorage.setItem('dismissed_popups', JSON.stringify(dismissedPopups));
      setPopupAnnouncement(null);
    }
  };

  const filteredJobs = jobs.filter(job => {
    const query = searchQuery.toLowerCase();
    const location = locationQuery.toLowerCase();
    const matchesSearch = !searchQuery || 
      job.title?.toLowerCase().includes(query) ||
      job.position_name?.toLowerCase().includes(query) ||
      job.company?.toLowerCase().includes(query) ||
      job.skills?.some(s => s.toLowerCase().includes(query));
    const matchesLocation = !locationQuery ||
      job.location?.toLowerCase().includes(location) ||
      job.work_type?.toLowerCase().includes(location);
    return matchesSearch && matchesLocation;
  });

  const handleSearch = () => {
    trackSearch(`${searchQuery} ${locationQuery}`, filteredJobs.length);
  };

  const formatNumber = (num) => {
    if (num >= 1000) {
      return (num / 1000).toFixed(num >= 10000 ? 0 : 1) + 'K+';
    }
    return num.toString() + '+';
  };

  const handleJobClick = (job) => {
    setSelectedJob(job);
    setShowLoginPrompt(true);
  };

  const quickFilters = [
    { label: 'Remote Jobs', filter: 'remote' },
    { label: 'Work From Home', filter: 'wfh' },
    { label: 'Full Time', filter: 'full-time' },
    { label: 'Part Time', filter: 'part-time' },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Popup Announcement */}
      {popupAnnouncement && (
        <Dialog open={!!popupAnnouncement} onOpenChange={() => dismissPopup()}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{popupAnnouncement.title}</DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <p className="text-gray-600">{popupAnnouncement.content}</p>
              {popupAnnouncement.cta_text && popupAnnouncement.cta_link && (
                <Button className="mt-4 w-full" onClick={() => { window.open(popupAnnouncement.cta_link, '_blank'); dismissPopup(); }}>
                  {popupAnnouncement.cta_text}
                </Button>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Header */}
      <header className="bg-[#0B1F3A] sticky top-0 z-50 border-b border-[#1a3a5c]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3">
            <img src="/logo.png?v=46" alt="OC" className="h-9 w-auto" />
            <span className="text-white font-bold text-lg tracking-tight hidden sm:block">Officium Connect</span>
          </Link>
          
          {/* Desktop Search */}
          <div className="hidden lg:flex items-center bg-[#162d4a] border border-[#2a4a6a] rounded-full px-4 py-2.5 flex-1 max-w-lg mx-8">
            <Search className="h-4 w-4 text-slate-400 mr-3" />
            <input
              type="text"
              placeholder="Search jobs, roles or companies..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="bg-transparent text-white placeholder-slate-400 focus:outline-none text-sm flex-1"
            />
          </div>
          
          {/* Navigation */}
          <div className="flex items-center gap-3 md:gap-5">
            <Link to="/refer" className="text-slate-300 hover:text-white text-sm font-medium hidden md:block transition-colors">
              Refer & Earn
            </Link>
            <Link to="/employer" className="text-slate-300 hover:text-white text-sm font-medium hidden md:block transition-colors">
              For Employers
            </Link>
            <Link to="/login" className="text-slate-300 hover:text-white text-sm font-medium hidden lg:block transition-colors">
              Sign In
            </Link>
            <Link to="/login">
              <Button className="bg-[#3B82F6] hover:bg-[#2563EB] text-white font-semibold text-sm px-5 py-2 rounded-full shadow-lg shadow-blue-500/25">
                Get Started
              </Button>
            </Link>
          </div>
        </div>
        
        {/* Mobile Search */}
        <div className="lg:hidden px-4 pb-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search jobs, roles or companies..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-[#162d4a] border border-[#2a4a6a] rounded-full text-white placeholder-slate-400 focus:outline-none text-sm"
            />
          </div>
        </div>
      </header>

      {/* Banners */}
      {banners.length > 0 && (
        <div className="bg-gradient-to-r from-amber-400 to-orange-400 px-4 py-2">
          <div className="max-w-7xl mx-auto text-center text-sm font-medium text-white">
            {banners[0]?.content}
          </div>
        </div>
      )}

      {/* Hero Section - New Design */}
      <section className="bg-[#0B1F3A] text-white py-14 md:py-20 relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-20 left-10 w-72 h-72 bg-[#3B82F6]/20 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-[#8B5CF6]/20 rounded-full blur-3xl"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
            <svg className="w-[600px] h-[600px] text-white/5" viewBox="0 0 100 100">
              <circle cx="50" cy="50" r="40" fill="none" stroke="currentColor" strokeWidth="0.5"/>
              <circle cx="50" cy="50" r="30" fill="none" stroke="currentColor" strokeWidth="0.5"/>
              <circle cx="50" cy="50" r="20" fill="none" stroke="currentColor" strokeWidth="0.5"/>
            </svg>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
          <div className="grid lg:grid-cols-5 gap-10 lg:gap-16 items-start">
            {/* Left Content - Takes 3 columns */}
            <div className="lg:col-span-3">
              {/* Trust Badge */}
              <div className="inline-flex items-center gap-2.5 bg-[#1a3a5c] border border-[#2a4a6a] px-4 py-2 rounded-full mb-8">
                <div className="w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center">
                  <CheckCircle className="w-3 h-3 text-white" />
                </div>
                <span className="text-sm text-slate-300 font-medium">Trusted by Thousands Across India</span>
              </div>

              <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-5 leading-[1.1] tracking-tight">
                Find Your Dream<br />
                <span className="text-[#60A5FA]">Career</span>
              </h1>
              
              <p className="text-lg text-slate-400 mb-10 max-w-xl leading-relaxed">
                India&#39;s trusted platform for verified careers - Remote & Office. Start your journey to success today.
              </p>

              {/* Search Box */}
              <div className="bg-white rounded-2xl p-1.5 shadow-2xl shadow-black/30 mb-8">
                <div className="flex flex-col md:flex-row">
                  <div className="flex-1 flex items-center gap-3 px-5 py-4 border-b md:border-b-0 md:border-r border-gray-100">
                    <Briefcase className="h-5 w-5 text-slate-400 flex-shrink-0" />
                    <input
                      type="text"
                      placeholder="Job title, skills or company"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="flex-1 focus:outline-none text-slate-700 text-sm font-medium placeholder-slate-400"
                    />
                  </div>
                  <div className="flex-1 flex items-center gap-3 px-5 py-4">
                    <MapPin className="h-5 w-5 text-slate-400 flex-shrink-0" />
                    <input
                      type="text"
                      placeholder="Location"
                      value={locationQuery}
                      onChange={(e) => setLocationQuery(e.target.value)}
                      className="flex-1 focus:outline-none text-slate-700 text-sm font-medium placeholder-slate-400"
                    />
                  </div>
                  <Button 
                    onClick={handleSearch}
                    className="bg-[#3B82F6] hover:bg-[#2563EB] text-white px-8 py-4 md:rounded-l-none rounded-xl font-semibold text-sm m-1 md:m-0 shadow-lg shadow-blue-500/30"
                  >
                    Find Jobs
                  </Button>
                </div>
              </div>

              {/* Quick Filters */}
              <div className="flex flex-wrap gap-3 mb-10">
                {quickFilters.map((filter, idx) => (
                  <button
                    key={idx}
                    onClick={() => setSearchQuery(filter.filter)}
                    className="px-5 py-2.5 bg-[#1a3a5c] hover:bg-[#243f5f] border border-[#2a4a6a] rounded-full text-sm text-slate-300 font-medium transition-all flex items-center gap-2"
                  >
                    {idx === 0 && <Laptop className="h-4 w-4" />}
                    {idx === 1 && <Home className="h-4 w-4" />}
                    {idx === 2 && <Clock className="h-4 w-4" />}
                    {idx === 3 && <Clock className="h-4 w-4" />}
                    {filter.label}
                  </button>
                ))}
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Users className="h-5 w-5 text-[#60A5FA]" />
                    <p className="text-2xl md:text-3xl font-bold text-white">{stats.active_jobs > 0 ? formatNumber(stats.active_jobs) : '100+'}</p>
                  </div>
                  <p className="text-sm text-slate-400">Active Jobs</p>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <CheckCircle className="h-5 w-5 text-emerald-400" />
                    <p className="text-2xl md:text-3xl font-bold text-white">{stats.verified_employers > 0 ? formatNumber(stats.verified_employers) : '20+'}</p>
                  </div>
                  <p className="text-sm text-slate-400">Verified Employers</p>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Users className="h-5 w-5 text-violet-400" />
                    <p className="text-2xl md:text-3xl font-bold text-white">{stats.job_seekers > 0 ? formatNumber(stats.job_seekers) : '2000+'}</p>
                  </div>
                  <p className="text-sm text-slate-400">Happy Job Seekers</p>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Star className="h-5 w-5 text-amber-400 fill-amber-400" />
                    <p className="text-2xl md:text-3xl font-bold text-white">{stats.user_rating}/5</p>
                  </div>
                  <p className="text-sm text-slate-400">User Rating</p>
                </div>
              </div>
            </div>

            {/* Right Content - Recommended Jobs - Takes 2 columns */}
            <div className="hidden lg:block lg:col-span-2 relative">
              {/* Verified Badge */}
              <div className="absolute -top-2 -right-2 z-20">
                <div className="bg-emerald-500 text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-lg flex items-center gap-1.5">
                  <CheckCircle className="h-3.5 w-3.5" />
                  Verified Opportunities
                </div>
              </div>

              <div className="bg-white rounded-2xl shadow-2xl shadow-black/20 p-6">
                <div className="flex items-center justify-between mb-5">
                  <h3 className="font-bold text-slate-800 text-lg">Recommended for you</h3>
                  <Link to="/login" className="text-[#3B82F6] text-sm font-medium hover:underline">View all</Link>
                </div>
                <div className="space-y-3">
                  {filteredJobs.length > 0 ? (
                    filteredJobs.slice(0, 4).map((job, idx) => (
                      <div 
                        key={job.id || idx}
                        onClick={() => handleJobClick(job)}
                        className="p-4 border border-slate-100 rounded-xl hover:border-[#3B82F6]/40 hover:shadow-lg hover:shadow-blue-500/10 transition-all cursor-pointer group"
                      >
                        <div className="flex items-start gap-3">
                          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-blue-50 to-slate-100 flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform">
                            {job.company_logo_url ? (
                              <img src={job.company_logo_url} alt="" className="w-6 h-6 object-contain" />
                            ) : (
                              <Building2 className="w-5 h-5 text-[#3B82F6]" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="font-semibold text-slate-800 text-sm truncate group-hover:text-[#3B82F6] transition-colors">{job.title || job.position_name}</h4>
                            <p className="text-xs text-slate-500 truncate mt-0.5">{job.company}</p>
                            <div className="flex items-center gap-2 mt-2.5">
                              <Badge className="text-[10px] px-2 py-0.5 bg-blue-50 text-blue-600 border-0 font-medium">{job.work_type || 'Full Time'}</Badge>
                              <span className="text-[10px] text-slate-400 flex items-center gap-1">
                                <MapPin className="h-3 w-3" />{job.location || 'Remote'}
                              </span>
                            </div>
                          </div>
                          <button className="opacity-0 group-hover:opacity-100 transition-opacity">
                            <svg className="w-5 h-5 text-slate-300 hover:text-[#3B82F6]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    ))
                  ) : (
                    /* Show placeholder jobs when no real jobs available */
                    [
                      { title: 'Senior Software Engineer', company: 'Tech Corp', work_type: 'Remote', location: 'Bangalore', salary: '₹18-25 LPA' },
                      { title: 'Product Manager', company: 'StartupXYZ', work_type: 'Hybrid', location: 'Mumbai', salary: '₹20-30 LPA' },
                      { title: 'UI/UX Designer', company: 'Design Studio', work_type: 'Office', location: 'Delhi', salary: '₹12-18 LPA' },
                      { title: 'Data Analyst', company: 'Analytics Inc', work_type: 'Remote', location: 'Pan India', salary: '₹10-15 LPA' },
                    ].map((job, idx) => (
                      <div 
                        key={idx}
                        onClick={() => navigate('/login')}
                        className="p-4 border border-slate-100 rounded-xl hover:border-[#3B82F6]/40 hover:shadow-lg hover:shadow-blue-500/10 transition-all cursor-pointer group"
                      >
                        <div className="flex items-start gap-3">
                          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-blue-50 to-slate-100 flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform">
                            <Building2 className="w-5 h-5 text-[#3B82F6]" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="font-semibold text-slate-800 text-sm truncate group-hover:text-[#3B82F6] transition-colors">{job.title}</h4>
                            <p className="text-xs text-slate-500 truncate mt-0.5">{job.company}</p>
                            <div className="flex items-center gap-2 mt-2.5">
                              <Badge className="text-[10px] px-2 py-0.5 bg-blue-50 text-blue-600 border-0 font-medium">{job.work_type}</Badge>
                              <span className="text-[10px] text-slate-400 flex items-center gap-1">
                                <MapPin className="h-3 w-3" />{job.location}
                              </span>
                            </div>
                          </div>
                          <button className="opacity-0 group-hover:opacity-100 transition-opacity">
                            <svg className="w-5 h-5 text-slate-300 hover:text-[#3B82F6]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-14">
            <h2 className="text-2xl md:text-4xl font-bold text-slate-800 mb-4 tracking-tight">
              The Smarter Way to Find Your Next Opportunity
            </h2>
            <p className="text-slate-500 max-w-2xl mx-auto text-base">
              We&#39;ve built the most trusted job platform with features designed for your success
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="border-0 shadow-lg hover:shadow-xl transition-all bg-white p-8 rounded-2xl text-center group hover:-translate-y-1">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-100 to-emerald-50 flex items-center justify-center mx-auto mb-5 group-hover:scale-110 transition-transform">
                <Shield className="h-8 w-8 text-emerald-600" />
              </div>
              <h3 className="font-bold text-lg text-slate-800 mb-3">100% Verified Jobs</h3>
              <p className="text-slate-500 text-sm leading-relaxed">All jobs are manually verified for authenticity. No scams, only genuine opportunities.</p>
            </Card>

            <Card className="border-0 shadow-lg hover:shadow-xl transition-all bg-white p-8 rounded-2xl text-center group hover:-translate-y-1">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-100 to-blue-50 flex items-center justify-center mx-auto mb-5 group-hover:scale-110 transition-transform">
                <Zap className="h-8 w-8 text-blue-600" />
              </div>
              <h3 className="font-bold text-lg text-slate-800 mb-3">Fast & Easy Apply</h3>
              <p className="text-slate-500 text-sm leading-relaxed">Simple application process that gets you noticed. Apply to multiple jobs with one click.</p>
            </Card>

            <Card className="border-0 shadow-lg hover:shadow-xl transition-all bg-white p-8 rounded-2xl text-center group hover:-translate-y-1">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-100 to-amber-50 flex items-center justify-center mx-auto mb-5 group-hover:scale-110 transition-transform">
                <Bell className="h-8 w-8 text-amber-600" />
              </div>
              <h3 className="font-bold text-lg text-slate-800 mb-3">Real-time Alerts</h3>
              <p className="text-slate-500 text-sm leading-relaxed">Get notified about new jobs that match your profile. Never miss an opportunity.</p>
            </Card>

            <Card className="border-0 shadow-lg hover:shadow-xl transition-all bg-white p-8 rounded-2xl text-center group hover:-translate-y-1">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-100 to-violet-50 flex items-center justify-center mx-auto mb-5 group-hover:scale-110 transition-transform">
                <HeadphonesIcon className="h-8 w-8 text-violet-600" />
              </div>
              <h3 className="font-bold text-lg text-slate-800 mb-3">Dedicated Support</h3>
              <p className="text-slate-500 text-sm leading-relaxed">Our team is here to help you at every step. From application to offer acceptance.</p>
            </Card>
          </div>
        </div>
      </section>

      {/* Jobs Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between mb-10">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-slate-800 tracking-tight">Latest Job Openings</h2>
              <p className="text-slate-500 mt-2">Explore {filteredJobs.length > 0 ? filteredJobs.length : 'thousands of'} opportunities waiting for you</p>
            </div>
            <Link to="/login">
              <Button variant="outline" className="hidden md:flex items-center gap-2 border-slate-200 hover:border-[#3B82F6] hover:text-[#3B82F6] font-medium">
                View All Jobs <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map(i => (
                <div key={i} className="animate-pulse bg-slate-100 rounded-2xl h-52"></div>
              ))}
            </div>
          ) : filteredJobs.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredJobs.slice(0, MAX_VISIBLE_JOBS).map((job, jobIndex) => (
                <React.Fragment key={`job-cell-${job.id}`}>
                {jobIndex === 6 && (
                  <div className="md:col-span-2 lg:col-span-3">
                    <AdSlot slot="listing" enoughContent={filteredJobs.length >= 6} className="my-0" />
                  </div>
                )}
                <Card 
                  key={job.id}
                  className="border border-slate-100 hover:border-[#3B82F6]/40 hover:shadow-xl hover:shadow-blue-500/10 transition-all cursor-pointer group rounded-2xl overflow-hidden"
                  onClick={() => handleJobClick(job)}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-50 to-slate-100 flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform">
                        {job.company_logo_url ? (
                          <img src={job.company_logo_url} alt="" className="w-8 h-8 object-contain" />
                        ) : (
                          <Building2 className="w-7 h-7 text-[#3B82F6]" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-slate-800 group-hover:text-[#3B82F6] transition-colors truncate text-base">
                          {job.title || job.position_name}
                        </h3>
                        <p className="text-sm text-slate-500 truncate mt-0.5">{job.company}</p>
                      </div>
                    </div>
                    
                    <div className="mt-5 flex flex-wrap items-center gap-3 text-xs text-slate-500">
                      <span className="flex items-center gap-1.5 bg-slate-50 px-2.5 py-1.5 rounded-lg">
                        <MapPin className="h-3.5 w-3.5" /> {job.location || 'Remote'}
                      </span>
                      <span className="flex items-center gap-1.5 bg-slate-50 px-2.5 py-1.5 rounded-lg">
                        <Clock className="h-3.5 w-3.5" /> {job.work_type || 'Full Time'}
                      </span>
                      {job.salary_range && (
                        <span className="flex items-center gap-1.5 bg-emerald-50 text-emerald-600 px-2.5 py-1.5 rounded-lg font-medium">
                          ₹ {job.salary_range}
                        </span>
                      )}
                    </div>

                    <div className="mt-5 flex items-center justify-between">
                      <div className="flex gap-2">
                        {job.skills?.slice(0, 2).map((skill, idx) => (
                          <Badge key={idx} variant="secondary" className="text-[10px] bg-slate-100 text-slate-600 font-medium">{skill}</Badge>
                        ))}
                      </div>
                      <Button size="sm" variant="ghost" className="text-[#3B82F6] hover:bg-[#3B82F6]/10 font-medium">
                        Apply <ChevronRight className="h-4 w-4 ml-1" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
                </React.Fragment>
              ))}
            </div>
          ) : (
            /* Show placeholder jobs when no real jobs */
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { title: 'Senior Software Engineer', company: 'Tech Corp', location: 'Bangalore', work_type: 'Remote', salary_range: '18-25 LPA', skills: ['React', 'Node.js'] },
                { title: 'Product Manager', company: 'StartupXYZ', location: 'Mumbai', work_type: 'Hybrid', salary_range: '20-30 LPA', skills: ['Agile', 'Strategy'] },
                { title: 'UI/UX Designer', company: 'Design Studio', location: 'Delhi', work_type: 'Office', salary_range: '12-18 LPA', skills: ['Figma', 'UI'] },
                { title: 'Data Analyst', company: 'Analytics Inc', location: 'Pan India', work_type: 'Remote', salary_range: '10-15 LPA', skills: ['SQL', 'Python'] },
                { title: 'Marketing Manager', company: 'Brand Co', location: 'Hyderabad', work_type: 'Hybrid', salary_range: '15-22 LPA', skills: ['SEO', 'Growth'] },
                { title: 'DevOps Engineer', company: 'Cloud Systems', location: 'Pune', work_type: 'Remote', salary_range: '16-24 LPA', skills: ['AWS', 'Docker'] },
              ].map((job, idx) => (
                <Card 
                  key={idx}
                  className="border border-slate-100 hover:border-[#3B82F6]/40 hover:shadow-xl hover:shadow-blue-500/10 transition-all cursor-pointer group rounded-2xl overflow-hidden"
                  onClick={() => navigate('/login')}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-50 to-slate-100 flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform">
                        <Building2 className="w-7 h-7 text-[#3B82F6]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-slate-800 group-hover:text-[#3B82F6] transition-colors truncate text-base">
                          {job.title}
                        </h3>
                        <p className="text-sm text-slate-500 truncate mt-0.5">{job.company}</p>
                      </div>
                    </div>
                    
                    <div className="mt-5 flex flex-wrap items-center gap-3 text-xs text-slate-500">
                      <span className="flex items-center gap-1.5 bg-slate-50 px-2.5 py-1.5 rounded-lg">
                        <MapPin className="h-3.5 w-3.5" /> {job.location}
                      </span>
                      <span className="flex items-center gap-1.5 bg-slate-50 px-2.5 py-1.5 rounded-lg">
                        <Clock className="h-3.5 w-3.5" /> {job.work_type}
                      </span>
                      <span className="flex items-center gap-1.5 bg-emerald-50 text-emerald-600 px-2.5 py-1.5 rounded-lg font-medium">
                        ₹ {job.salary_range}
                      </span>
                    </div>

                    <div className="mt-5 flex items-center justify-between">
                      <div className="flex gap-2">
                        {job.skills.map((skill, sidx) => (
                          <Badge key={sidx} variant="secondary" className="text-[10px] bg-slate-100 text-slate-600 font-medium">{skill}</Badge>
                        ))}
                      </div>
                      <Button size="sm" variant="ghost" className="text-[#3B82F6] hover:bg-[#3B82F6]/10 font-medium">
                        Apply <ChevronRight className="h-4 w-4 ml-1" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          <div className="text-center mt-10">
            <Link to="/login">
              <Button size="lg" className="bg-[#3B82F6] hover:bg-[#2563EB] shadow-lg shadow-blue-500/25 font-semibold px-8">
                View All Jobs <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Vision/Mission Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-14">
            <h2 className="text-2xl md:text-4xl font-bold text-slate-800 mb-4 tracking-tight">
              Scam-free & Trusted Way to Find Work
            </h2>
            <p className="text-slate-500 text-base">Why thousands are switching to Officium Connect</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="border-0 shadow-lg hover:shadow-xl transition-all rounded-2xl overflow-hidden group hover:-translate-y-1">
              <CardContent className="p-8">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-100 to-emerald-50 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <svg className="h-8 w-8 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                </div>
                <h3 className="font-bold text-xl text-slate-800 mb-3">Our Vision</h3>
                <p className="text-slate-500 leading-relaxed text-sm">
                  Our vision is to help every candidate find the right job based on their skills and qualifications, with a fair salary. We bridge the gap for those who struggle to find their dream job.
                </p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg hover:shadow-xl transition-all rounded-2xl overflow-hidden group hover:-translate-y-1">
              <CardContent className="p-8">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-100 to-blue-50 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <svg className="h-8 w-8 text-[#3B82F6]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <h3 className="font-bold text-xl text-slate-800 mb-3">Our Mission</h3>
                <p className="text-slate-500 leading-relaxed text-sm">
                  To provide job opportunities for everyone, whether work from home or office. Through comprehensive training and end-to-end support, we match candidates to suitable jobs.
                </p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg hover:shadow-xl transition-all rounded-2xl overflow-hidden group hover:-translate-y-1">
              <CardContent className="p-8">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-100 to-violet-50 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <svg className="h-8 w-8 text-violet-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                </div>
                <h3 className="font-bold text-xl text-slate-800 mb-3">Our Support</h3>
                <p className="text-slate-500 leading-relaxed text-sm">
                  We&#39;ll help you every step of the way, from the first interview to the final offer, ensuring you&#39;re fully prepared to succeed in your new career.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Sponsored - kept between sections, far from any button */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <AdSlot slot="listing" label="Sponsored" />
      </div>

      {/* CTA Section */}
      <section className="py-20 bg-[#0B1F3A] relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-10 left-10 w-64 h-64 bg-[#3B82F6]/20 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-80 h-80 bg-[#8B5CF6]/20 rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center relative z-10">
          <h2 className="text-2xl md:text-4xl font-bold text-white mb-5 tracking-tight">
            Ready to Start Your Career Journey?
          </h2>
          <p className="text-slate-400 mb-10 text-lg max-w-2xl mx-auto">
            Join thousands of job seekers who found their dream career through Officium Connect
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/login">
              <Button size="lg" className="bg-[#3B82F6] hover:bg-[#2563EB] text-white font-semibold px-10 py-6 text-base shadow-lg shadow-blue-500/30 rounded-xl">
                Get Started Free
              </Button>
            </Link>
            <Link to="/employer">
              <Button size="lg" variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-800 hover:text-white font-semibold px-10 py-6 text-base rounded-xl">
                Post a Job
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#070F1A] text-white py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-10 mb-10">
            <div>
              <h4 className="font-bold mb-5 text-white">For Job Seekers</h4>
              <ul className="space-y-3 text-sm text-slate-400">
                <li><Link to="/login" className="hover:text-white transition-colors">Browse Jobs</Link></li>
                <li><Link to="/login" className="hover:text-white transition-colors">Create Profile</Link></li>
                <li><Link to="/refer" className="hover:text-white transition-colors">Refer & Earn</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-5 text-white">For Employers</h4>
              <ul className="space-y-3 text-sm text-slate-400">
                <li><Link to="/employer" className="hover:text-white transition-colors">Post a Job</Link></li>
                <li><Link to="/employer" className="hover:text-white transition-colors">Employer Login</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-5 text-white">Company</h4>
              <ul className="space-y-3 text-sm text-slate-400">
                <li><a href="#" className="hover:text-white transition-colors">About Us</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Contact</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-5 text-white">Connect</h4>
              <ul className="space-y-3 text-sm text-slate-400">
                {footerConfig?.email && <li><a href={`mailto:${footerConfig.email}`} className="hover:text-white transition-colors">{footerConfig.email}</a></li>}
                {footerConfig?.phone && <li><a href={`tel:${footerConfig.phone}`} className="hover:text-white transition-colors">{footerConfig.phone}</a></li>}
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 pt-8 text-center text-sm text-slate-500">
            <p>&copy; {new Date().getFullYear()} {footerConfig?.company_name || 'Officium Connect'}. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Login Prompt Dialog */}
      <Dialog open={showLoginPrompt} onOpenChange={setShowLoginPrompt}>
        <DialogContent className="max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg">
              <Lock className="h-5 w-5 text-[#3B82F6]" />
              Sign in to Apply
            </DialogTitle>
          </DialogHeader>
          {selectedJob && (
            <div className="py-4">
              <div className="p-4 bg-slate-50 rounded-xl mb-5">
                <h3 className="font-bold text-slate-800">{selectedJob.title || selectedJob.position_name}</h3>
                <p className="text-sm text-slate-500 mt-0.5">{selectedJob.company}</p>
                <p className="text-sm text-slate-500 mt-2 flex items-center gap-1.5">
                  <MapPin className="h-3.5 w-3.5" /> {selectedJob.location || 'Remote'}
                </p>
              </div>
              <p className="text-slate-600 text-sm mb-5">
                Create a free account to apply for this job and get access to:
              </p>
              <ul className="space-y-3 text-sm text-slate-600 mb-6">
                <li className="flex items-center gap-2.5">
                  <CheckCircle className="h-4.5 w-4.5 text-emerald-500" /> One-click applications
                </li>
                <li className="flex items-center gap-2.5">
                  <CheckCircle className="h-4.5 w-4.5 text-emerald-500" /> Real-time job alerts
                </li>
                <li className="flex items-center gap-2.5">
                  <CheckCircle className="h-4.5 w-4.5 text-emerald-500" /> Application tracking
                </li>
              </ul>
              <div className="flex gap-3">
                <Link to="/login" className="flex-1">
                  <Button className="w-full bg-[#3B82F6] hover:bg-[#2563EB] font-semibold shadow-lg shadow-blue-500/25">Sign In</Button>
                </Link>
                <Button variant="outline" onClick={() => setShowLoginPrompt(false)} className="border-slate-200">
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <InstallPrompt />
    </div>
  );
};

export default PublicHome;
